import { useMemo } from 'react';
import { LocationFeatureResponse } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FeatureFlagStrategy } from '../types';
import { useAppFlagStore } from './use-feature-flag';

export const MESSAGING_AIR_CUSTOMIZATION_FLAGS = ['truelarkaimessaging', 'aimessaging'] as const;
export const MESSAGING_AIR_CUSTOMIZATION_FEATURE_ENUM = Feature.TRUELARK_AI_MESSAGING;
const DEFAULT_ACTIVE_STATES = [State.ACTIVE];

export const useMessagingAIRCustomizationFlag = (
  locationIds: string[],
  strategy: FeatureFlagStrategy = 'ANY',
  activeStates: State[] = DEFAULT_ACTIVE_STATES
) => {
  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  return useMemo(() => {
    if (locationIds.length === 0) {
      return {
        isActive: false,
        locationFlagMap: {},
        locationFlagValueMap: {},
      };
    }

    const rawLocationFlagMap = getCustomizationFlagLocationMap({
      locationIds,
      featureEnum: MESSAGING_AIR_CUSTOMIZATION_FEATURE_ENUM,
    });

    const locationFlagMap: Record<string, LocationFeatureResponse> = {};
    const locationFlagValueMap: Record<string, boolean> = {};

    let hasResolvedFlags = false;
    let anyActive = false;
    let allActive = true;

    for (const locationId in rawLocationFlagMap) {
      const flagData = rawLocationFlagMap[locationId];
      if (!flagData) {
        continue;
      }

      hasResolvedFlags = true;
      locationFlagMap[locationId] = flagData;

      const isLocationActive = activeStates.includes(flagData.state ?? State.HIDE);
      locationFlagValueMap[locationId] = isLocationActive;

      anyActive = anyActive || isLocationActive;
      allActive = allActive && isLocationActive;
    }

    const isActive = strategy === 'ALL' ? hasResolvedFlags && allActive : anyActive;

    return {
      isActive,
      locationFlagMap,
      locationFlagValueMap,
    };
  }, [activeStates, getCustomizationFlagLocationMap, locationIds, strategy]);
};
